import { AppButton, AppInput, AppSelect, AppTextArea } from "components/antd-ui";
import { CommentThread } from "components/Comment/CommentThread";
import { customerApi } from "api/customer.api";
import { opportunityApi } from "api/opportunity.api";
import { debounce } from "lodash";
import { observer } from "mobx-react";
import { useCallback, useEffect, useMemo, useRef, useState, type ChangeEvent, type ReactNode } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { message, Popconfirm, Spin } from "antd";
import { Customer } from "types/customer";
import { CommentRefType } from "types/comment";
import {
  Opportunity,
  OpportunityFile,
  OpportunityStatus,
  QuoteStatus,
} from "types/opportunity";
import { getTitle } from "utils";
import { can } from "utils/auth";
import { CustomerModal } from "views/Customer/components/CustomerModal";
import { OpportunityFiles } from "./components/OpportunityFiles";
import { OpportunityLinks } from "./components/OpportunityLinks";
import { ProductTable, createProductRow, type ProductRow } from "./components/ProductTable";
import { ORDER_PATH } from "views/Order/order.meta";
import {
  OPPORTUNITY_PATH,
  QUOTE_PATH,
  opportunityStatusOptions,
  quoteStatusOptions,
} from "./opportunity.meta";
import "./OpportunityForm.scss";
import { CheckCircleOutlined, CloseCircleOutlined, PlusOutlined, SaveOutlined } from "@ant-design/icons";
import { userStore } from "store/userStore";

type CustomerOption = { label: string; value: number; className?: string };

const Field = ({
  label,
  required,
  wide,
  extra,
  children,
}: {
  label: string;
  required?: boolean;
  wide?: boolean;
  extra?: ReactNode;
  children: ReactNode;
}) => (
  <div className={`opportunityForm__field${wide ? " isWide" : ""}`}>
    <span className="opportunityForm__label">
      {label}
      {required ? <span className="opportunityForm__required"> *</span> : null}
      {extra ? <span className="opportunityForm__labelExtra">{extra}</span> : null}
    </span>
    <div className="opportunityForm__control">{children}</div>
  </div>
);

const toRows = (opportunity?: Opportunity): ProductRow[] => {
  const details = opportunity?.opportunityDetails || [];
  if (!details.length) return [createProductRow()];
  return details.map((detail) => ({
    key: `id-${detail.id}`,
    id: detail.id,
    description: detail.description || "",
    unit: detail.unit || "",
    quantity: detail.quantity ?? null,
    price: detail.price ?? null,
  }));
};

export const OpportunityFormPage = observer(() => {
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const isCreate = !id;
  const opportunityId = id ? Number(id) : 0;

  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [name, setName] = useState("");
  const [code, setCode] = useState("");
  const [customerId, setCustomerId] = useState<number>();
  const [customerDescription, setCustomerDescription] = useState("");
  const [internalNote, setInternalNote] = useState("");
  const [status, setStatus] = useState<OpportunityStatus>(OpportunityStatus.GetRequest);
  const [savedStatus, setSavedStatus] = useState<OpportunityStatus | null>(null);
  const [quoteStatus, setQuoteStatus] = useState<QuoteStatus>();
  const [savedQuoteStatus, setSavedQuoteStatus] = useState<QuoteStatus | null>(null);
  const [orderId, setOrderId] = useState<number | null>(null);
  // Người tạo: chỉ hiển thị (backend tự gán người đăng nhập khi tạo)
  const [createdStaffName, setCreatedStaffName] = useState("");
  const [failReason, setFailReason] = useState("");
  const [customerFiles, setCustomerFiles] = useState<OpportunityFile[]>([]);
  const [internalFiles, setInternalFiles] = useState<OpportunityFile[]>([]);
  const [links, setLinks] = useState<string[]>([]);
  const [rows, setRows] = useState<ProductRow[]>([createProductRow()]);
  const [customerOptions, setCustomerOptions] = useState<CustomerOption[]>([]);

  const selectedCustomerRef = useRef<CustomerOption>();
  const customerOptionsRef = useRef<CustomerOption[]>([]);
  const customerModalRef = useRef<CustomerModal>(null);
  customerOptionsRef.current = customerOptions;

  const canSave = isCreate
    ? can(OPPORTUNITY_PATH, "CREATE")
    : can(OPPORTUNITY_PATH, "UPDATE") || can(QUOTE_PATH, "UPDATE");
  const readOnly = !canSave;
  const canCreateCustomer = can("/customer", "CREATE");
  const showQuoteStatus = !!savedQuoteStatus;
  const quoteLocked = savedQuoteStatus === QuoteStatus.CloseOrder;
  const canApprove =
    !isCreate &&
    !savedQuoteStatus &&
    savedStatus === OpportunityStatus.WaitApprove &&
    can(OPPORTUNITY_PATH, "APPROVE");
  const canDecideQuote =
    !isCreate && savedQuoteStatus === QuoteStatus.WaitApprove && can(QUOTE_PATH, "APPROVE");
  const canViewOrder = quoteLocked && !!orderId && can(ORDER_PATH, "VIEW");
  const showFailReason = quoteStatus === QuoteStatus.Failed;

  const applyOpportunity = useCallback((opportunity: Opportunity) => {
    setName(opportunity.name || "");
    setCode(opportunity.code || "");
    setCustomerDescription(opportunity.customerDescription || "");
    setInternalNote(opportunity.internalNote || "");
    setStatus(opportunity.status || OpportunityStatus.GetRequest);
    setSavedStatus(opportunity.status || OpportunityStatus.GetRequest);
    setQuoteStatus(opportunity.quoteStatus || undefined);
    setSavedQuoteStatus(opportunity.quoteStatus);
    setOrderId(opportunity.orderId || null);
    setFailReason(opportunity.failReason || "");
    setCustomerFiles(opportunity.customerFiles || []);
    setInternalFiles(opportunity.internalFiles || []);
    setLinks(opportunity.links || []);
    setRows(toRows(opportunity));
    setCreatedStaffName(opportunity.createdStaff?.name || opportunity.createdStaff?.username || "");
    if (opportunity.customer) {
      const option = { label: opportunity.customer.name, value: opportunity.customer.id };
      setCustomerId(opportunity.customer.id);
      selectedCustomerRef.current = option;
      setCustomerOptions((prev) =>
        prev.some((item) => item.value === option.value) ? prev : [option, ...prev],
      );
    }
  }, []);

  const fetchCustomers = useCallback(async (keyword: string) => {
    const res = await customerApi.findAll({ page: 1, limit: 20, search: keyword });
    const customers: Customer[] = res.data.customers || [];
    const options = customers.map((customer) => ({
      label: customer.name,
      value: customer.id,
    }));
    const selected = selectedCustomerRef.current;
    if (selected && !options.some((option) => option.value === selected.value)) {
      setCustomerOptions([selected, ...options]);
      return;
    }
    setCustomerOptions(options);
  }, []);

  const debounceSearchCustomer = useMemo(
    () =>
      debounce((keyword: string) => {
        fetchCustomers(keyword);
      }, 500),
    [fetchCustomers],
  );

  useEffect(() => {
    document.title = getTitle(isCreate ? "Tạo cơ hội" : savedQuoteStatus ? `Báo giá #${code || ''}` : "Cập nhật cơ hội");
  }, [isCreate, setSavedQuoteStatus, code]);

  useEffect(() => {
    fetchCustomers("");
    return () => {
      debounceSearchCustomer.cancel();
    };
  }, [debounceSearchCustomer, fetchCustomers]);

  useEffect(() => {
    if (!opportunityId) return;

    let ignore = false;
    const load = async () => {
      setLoading(true);
      try {
        const res = await opportunityApi.findOne(opportunityId);
        if (!ignore) applyOpportunity(res.data as Opportunity);
      } finally {
        if (!ignore) setLoading(false);
      }
    };
    load();

    return () => {
      ignore = true;
    };
  }, [applyOpportunity, opportunityId]);

  useEffect(() => {
    if (!opportunityId || !savedQuoteStatus) return;
    if (!location.pathname.startsWith(`${OPPORTUNITY_PATH}/`)) return;
    navigate(`${QUOTE_PATH}/${opportunityId}`, { replace: true });
  }, [location.pathname, navigate, opportunityId, savedQuoteStatus]);

  const handleNameChange = useCallback((event: ChangeEvent<HTMLInputElement>) => {
    setName(event.target.value);
  }, []);

  const handleCodeChange = useCallback((event: ChangeEvent<HTMLInputElement>) => {
    setCode(event.target.value);
  }, []);

  const openQuickCreateCustomer = useCallback(() => {
    customerModalRef.current?.handleCreate();
  }, []);

  const handleCustomerChange = useCallback((value: number | undefined) => {
    setCustomerId(value);
    selectedCustomerRef.current = value
      ? customerOptionsRef.current.find((option) => option.value === value)
      : undefined;
  }, []);

  const handleCustomerCreated = useCallback((customer?: Customer) => {
    if (!customer?.id) return;
    const option = { label: customer.name, value: customer.id };
    selectedCustomerRef.current = option;
    setCustomerId(customer.id);
    setCustomerOptions((prev) =>
      prev.some((item) => item.value === option.value) ? prev : [option, ...prev],
    );
  }, []);

  const handleStatusChange = useCallback((value: OpportunityStatus) => {
    setStatus(value);
  }, []);

  const handleQuoteStatusChange = useCallback((value: QuoteStatus) => {
    setQuoteStatus(value);
  }, []);

  const handleDescriptionChange = useCallback((event: ChangeEvent<HTMLTextAreaElement>) => {
    setCustomerDescription(event.target.value);
  }, []);

  const handleNoteChange = useCallback((event: ChangeEvent<HTMLTextAreaElement>) => {
    setInternalNote(event.target.value);
  }, []);

  const handleFailReasonChange = useCallback((event: ChangeEvent<HTMLTextAreaElement>) => {
    setFailReason(event.target.value);
  }, []);

  const goBack = useCallback(() => {
    const onQuote = location.pathname.startsWith(`${QUOTE_PATH}/`);
    navigate(onQuote ? QUOTE_PATH : OPPORTUNITY_PATH);
  }, [location.pathname, navigate]);

  const handleSave = useCallback(async () => {
    if (!name.trim()) {
      message.warning("Nhập tên cơ hội");
      return;
    }
    if (!customerId) {
      message.warning("Chọn khách hàng");
      return;
    }
    if (status === OpportunityStatus.WaitApprove && !code.trim()) {
      message.warning("Nhập mã báo giá");
      return;
    }
    if (showQuoteStatus && !quoteStatus) {
      message.warning("Chọn trạng thái báo giá");
      return;
    }
    if (showFailReason && !failReason.trim()) {
      message.warning("Nhập lý do thất bại");
      return;
    }

    const payload = {
      customerId,
      opportunity: {
        name: name.trim(),
        code: code.trim(),
        customerDescription,
        internalNote,
        status,
        quoteStatus: showQuoteStatus ? quoteStatus || savedQuoteStatus : null,
        failReason: showFailReason ? failReason.trim() : "",
        customerFiles,
        internalFiles,
        links: links.map((link) => link.trim()).filter(Boolean),
        opportunityDetails: rows
          .filter((row) => row.description.trim() || row.unit.trim() || row.quantity || row.price)
          .map((row) => ({
            id: row.id,
            description: row.description,
            unit: row.unit,
            quantity: row.quantity || 0,
            price: row.price || 0,
          })),
      },
    };

    setSaving(true);
    try {
      if (isCreate) {
        const res = await opportunityApi.create(payload);
        message.success("Tạo cơ hội thành công");
        const created = res.data as Opportunity;
        navigate(`${OPPORTUNITY_PATH}/${created.id}`, { replace: true, state: { from: "opportunity" } });
        return;
      }
      const res = await opportunityApi.update(opportunityId, payload);
      message.success("Lưu thành công");
      applyOpportunity(res.data as Opportunity);
    } finally {
      setSaving(false);
    }
  }, [
    applyOpportunity,
    code,
    customerDescription,
    customerFiles,
    customerId,
    failReason,
    internalFiles,
    internalNote,
    isCreate,
    links,
    name,
    navigate,
    opportunityId,
    quoteStatus,
    rows,
    showFailReason,
    showQuoteStatus,
    status,
  ]);

  const handleApprove = useCallback(async () => {
    if (!opportunityId) return;
    setSaving(true);
    try {
      const res = await opportunityApi.approve(opportunityId);
      message.success("Đã duyệt báo giá");
      applyOpportunity(res.data as Opportunity);
      navigate(`${QUOTE_PATH}/${opportunityId}`, { replace: true });
    } finally {
      setSaving(false);
    }
  }, [applyOpportunity, navigate, opportunityId]);

  const handleCloseOrder = useCallback(async () => {
    if (!opportunityId) return;
    setSaving(true);
    try {
      const res = await opportunityApi.closeOrder(opportunityId);
      const closed = res.data as Opportunity;
      message.success("Đã duyệt lên đơn");
      applyOpportunity(closed);
      // Báo giá chốt đơn đã thành Đơn hàng → chuyển sang chi tiết đơn hàng vừa tạo
      if (closed.orderId && can(ORDER_PATH, "VIEW")) {
        navigate(`${ORDER_PATH}?orderId=${closed.orderId}`, { replace: true });
      }
    } finally {
      setSaving(false);
    }
  }, [applyOpportunity, navigate, opportunityId]);

  const handleReject = useCallback(async () => {
    if (!opportunityId) return;
    setSaving(true);
    try {
      const res = await opportunityApi.reject(opportunityId);
      message.success("Đã từ chối, quay về chờ gửi khách");
      applyOpportunity(res.data as Opportunity);
    } finally {
      setSaving(false);
    }
  }, [applyOpportunity, opportunityId]);

  const openOrder = useCallback(() => {
    if (!orderId) return;
    navigate(`${ORDER_PATH}?orderId=${orderId}`);
  }, [navigate, orderId]);

  const heading = isCreate ? "Tạo cơ hội" : savedQuoteStatus ? "Báo giá" : "Cập nhật cơ hội";
  const hideSave = !savedQuoteStatus && savedStatus === OpportunityStatus.WaitApprove;

  return (
    <Spin spinning={loading}>
      <div className="opportunityForm">
        <div className="opportunityForm__bar">
          <div>
            <h2 className="opportunityForm__title">{heading}</h2>
          </div>
          <div className="opportunityForm__actions">
            <AppButton onClick={goBack}>Quay về</AppButton>
            {canApprove && (
              <Popconfirm
                classNames={{ root: "opportunityApproveConfirm" }}
                cancelText="Không"
                okText="Duyệt"
                onConfirm={handleApprove}
                title="Xác nhận duyệt báo giá?"
              >
                <AppButton loading={saving} icon={<CheckCircleOutlined />} tone="primary">Duyệt báo giá</AppButton>
              </Popconfirm>
            )}
            {canDecideQuote && (
              <Popconfirm
                cancelText="Không"
                okText="Từ chối"
                onConfirm={handleReject}
                title="Từ chối và quay về chờ gửi khách?"
              >
                <AppButton loading={saving} icon={<CloseCircleOutlined />} tone="danger">Từ chối</AppButton>
              </Popconfirm>
            )}
            {canDecideQuote && (
              <Popconfirm
                cancelText="Không"
                okText="Duyệt"
                onConfirm={handleCloseOrder}
                title="Duyệt lên đơn và chốt đơn?"
              >
                <AppButton loading={saving} icon={<CheckCircleOutlined />} tone="primary">Duyệt lên đơn</AppButton>
              </Popconfirm>
            )}
            {canViewOrder && (
              <AppButton onClick={openOrder}>Xem đơn hàng</AppButton>
            )}
            {canSave && !hideSave && (
              <AppButton tone="primary" loading={saving} onClick={handleSave} icon={<SaveOutlined />} >
                Lưu
              </AppButton>
            )}
          </div>
        </div>

        <section className="opportunityForm__section">
          <h3 className="opportunityForm__sectionTitle">Thông tin chung</h3>
          <div className="opportunityForm__grid">
            <Field
              label="Khách hàng"
              required
              extra={
                !readOnly && canCreateCustomer ? (
                  <button type="button" className="opportunityForm__quickAdd" onClick={openQuickCreateCustomer}>
                    <PlusOutlined /> Thêm nhanh
                  </button>
                ) : null
              }
            >
              <AppSelect
                value={customerId}
                options={customerOptions}
                placeholder="Chọn khách hàng"
                showSearch
                filterOption={false}
                onSearch={debounceSearchCustomer}
                onChange={handleCustomerChange}
                disabled={readOnly}
                popupClassName="opportunityForm__customerDropdown"
              />
            </Field>
            {!showQuoteStatus && (
              <Field label="Tên cơ hội" required>
                <AppInput value={name} onChange={handleNameChange} disabled={readOnly} />
              </Field>
            )}
            <Field label="Mã báo giá" required={status === OpportunityStatus.WaitApprove}>
              <AppInput value={code} onChange={handleCodeChange} disabled={readOnly} />
            </Field>
            {!showQuoteStatus && (
              <Field label="Trạng thái cơ hội" required>
                <AppSelect
                  value={status}
                  options={opportunityStatusOptions}
                  onChange={handleStatusChange}
                  disabled={readOnly}
                />
              </Field>
            )}
            {showQuoteStatus && (
              <Field label="Trạng thái báo giá" required>
                <AppSelect
                  value={quoteStatus}
                  options={quoteStatusOptions}
                  placeholder="Chọn trạng thái báo giá"
                  onChange={handleQuoteStatusChange}
                  disabled={readOnly || quoteLocked}
                />
              </Field>
            )}
            <Field label="Người tạo">
              <AppInput
                value={isCreate ? userStore.info.name || userStore.info.username || "" : createdStaffName || "--"}
                disabled
              />
            </Field>
            {showFailReason && (
              <div className="opportunityForm__failReason">
                <Field label="Lý do thất bại" required>
                  <AppTextArea
                    value={failReason}
                    onChange={handleFailReasonChange}
                    disabled={readOnly}
                    rows={3}
                    placeholder="Nhập lý do thất bại"
                  />
                </Field>
              </div>
            )}
            <Field label="Mô tả của khách">
              <AppTextArea
                value={customerDescription}
                onChange={handleDescriptionChange}
                disabled={readOnly}
                rows={3}
              />
            </Field>
            <Field label="Ghi chú nội bộ">
              <AppTextArea value={internalNote} onChange={handleNoteChange} disabled={readOnly} rows={3} />
            </Field>
            <div className="opportunityForm__attach">
              <Field label="Link đính kèm">
                <OpportunityLinks value={links} onChange={setLinks} readOnly={readOnly} />
              </Field>
              <div className="opportunityForm__files">
                <Field label="File từ khách hàng">
                  <OpportunityFiles value={customerFiles} onChange={setCustomerFiles} readOnly={readOnly} />
                </Field>
                <Field label="File nội bộ">
                  <OpportunityFiles value={internalFiles} onChange={setInternalFiles} readOnly={readOnly} />
                </Field>
              </div>
            </div>
          </div>
        </section>

        <section className="opportunityForm__section">
          <h3 className="opportunityForm__sectionTitle">Thông tin hàng hóa</h3>
          <ProductTable rows={rows} onChange={setRows} readOnly={readOnly} />
        </section>

        {!isCreate && opportunityId > 0 && (
          <section className="opportunityForm__section">
            <h3 className="opportunityForm__sectionTitle">Bình luận</h3>
            <CommentThread refType={CommentRefType.Opportunity} refId={opportunityId} code={code} />
          </section>
        )}
      </div>
      <CustomerModal ref={customerModalRef} onSubmitOk={handleCustomerCreated} onClose={() => {}} />
    </Spin>
  );
});
